import React, { useEffect, useRef, useState } from 'react';
import Tag from '../atoms/Tag';
import FieldLabel from '../atoms/FieldLabel';

/**
 * Multi-select: dropdown with checkboxes + removable tags.
 * The dropdown closes on any click outside the field, or on Escape.
 */
export default function RoleMultiSelect({
  label = '',
  options = [],
  value = [],
  onChange,
  placeholder = 'Select roles',
  className = '',
  hasError = false,
  required = false,
  emptyText = 'No roles available',
  openDirection = 'up',
  showAll = true,
  maxVisible = null,
}) {
  const [open, setOpen] = useState(false);
  // Ref is on the field + menu only (not the label/tags), so any click outside closes it.
  const fieldRef = useRef(null);
  const menuRef = useRef(null);

  const normalizedOptions = options.map((opt) => (
    typeof opt === 'string'
      ? { value: opt, label: opt }
      : { value: opt.value, label: opt.label }
  ));

  const optionMap = new Map(
    normalizedOptions.map((opt) => [String(opt.value), opt.label]),
  );

  const isSelected = (optionValue) => value.some(
    (selectedValue) => String(selectedValue) === String(optionValue),
  );

  const allValues = normalizedOptions.map((opt) => opt.value);
  const allSelected = allValues.length > 0 && allValues.every((optionValue) => isSelected(optionValue));

    const handleToggleAll = () => {
    if (allSelected) {
      // Un-tick only the options shown here; keep any other selected values
      onChange?.(value.filter((selectedValue) => !allValues.some((v) => String(v) === String(selectedValue))));
    } else {
      const extras = value.filter((selectedValue) => !allValues.some((v) => String(v) === String(selectedValue)));
      onChange?.([...extras, ...allValues]);
    }
  };

    useEffect(() => {
    if (open && menuRef.current) menuRef.current.scrollTop = 0;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (fieldRef.current && !fieldRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleToggle = (optionValue) => {
    if (isSelected(optionValue)) {
      onChange?.(value.filter((r) => String(r) !== String(optionValue)));
    } else {
      onChange?.([...value, optionValue]);
    }
  };

  const handleRemove = (role) => {
    onChange?.(value.filter((r) => String(r) !== String(role)));
  };

  const summary = value.length ? `${value.length} selected` : placeholder;

  return (
    <div
      className={`ca-role-select ${className}`.trim()}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 8,
        width: '100%',
      }}
    >
      <FieldLabel>
        {label}
        {required ? (
          <span style={{ color: '#dc2626', marginLeft: 2 }} aria-hidden="true">*</span>
        ) : null}
      </FieldLabel>

      <div
        className="ca-multi-select"
        ref={fieldRef}
        style={{ position: 'relative', width: '100%' }}
      >
        <button
          type="button"
          className={`ca-multi-select__trigger ${hasError ? 'ca-control--invalid' : ''}`.trim()}
          style={{
            width: '100%',
            border: hasError ? '1px solid #dc2626' : undefined,
            boxShadow: hasError ? '0 0 0 3px rgba(220, 38, 38, 0.12)' : undefined,
          }}
          onClick={() => setOpen((current) => !current)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={value.length ? '' : 'ca-multi-select__placeholder'}>{summary}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {open ? (
          <ul
            ref={menuRef}
            className="ca-multi-select__menu"
            role="listbox"
            aria-multiselectable="true"
              style={{
              position: 'absolute',
              top: openDirection === 'down' ? 'calc(100% + 4px)' : 'auto',
              bottom: openDirection === 'down' ? 'auto' : 'calc(100% + 4px)',
              left: 0,
              right: 0,
              zIndex: 20,
              maxHeight: maxVisible ? maxVisible * 38 + 8 : (openDirection === 'down' ? 220 : 260),
              overflowY: 'auto',
            }}
          >
              {showAll && normalizedOptions.length > 1 ?  (
              <li role="option" aria-selected={allSelected}
              style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--ca-surface, #fff)', borderBottom: '1px solid var(--ca-border, #e5e7eb)' }}
              >
                <label className="ca-multi-select__option">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleToggleAll}
                  />
                  <span>All</span>
                </label>
              </li>
            ) : null}
            {normalizedOptions.length ? normalizedOptions.map((opt) => (
              <li key={String(opt.value)} role="option" aria-selected={isSelected(opt.value)}>
                <label className="ca-multi-select__option">
                  <input
                    type="checkbox"
                    checked={isSelected(opt.value)}
                    onChange={() => handleToggle(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              </li>
            )) : (
              <li className="ca-multi-select__empty">{emptyText}</li>
            )}
          </ul>
        ) : null}
      </div>

      {value.length > 0 ? (
        <div className="ca-tag-list">
          {[...new Set(value)].map((role) => (
            <Tag
              key={String(role)}
              label={optionMap.get(String(role)) || String(role)}
              onRemove={() => handleRemove(role)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}